/**
 * 접근성·키 배치. 세이브와 분리해 localStorage 에 둔다.
 */

export type BindAction =
  | 'left'
  | 'right'
  | 'up'
  | 'down'
  | 'jump'
  | 'attack'
  | 'skill1'
  | 'skill2'
  | 'skill3'
  | 'loot';

export const BIND_LABEL: Record<BindAction, string> = {
  left: '왼쪽',
  right: '오른쪽',
  up: '위',
  down: '아래',
  jump: '점프',
  attack: '공격',
  skill1: '스킬1',
  skill2: '스킬2',
  skill3: '스킬3',
  loot: '줍기',
};

export const BIND_ORDER: BindAction[] = [
  'left',
  'right',
  'up',
  'down',
  'jump',
  'attack',
  'skill1',
  'skill2',
  'skill3',
  'loot',
];

export interface UserSettings {
  shakeMul: number;
  damageScale: number;
  binds: Record<BindAction, string[]>;
}

const KEY = 'maple-slice.settings.v1';

export const DEFAULT_BINDS: Record<BindAction, string[]> = {
  left: ['LEFT'],
  right: ['RIGHT', 'D'],
  up: ['UP'],
  down: ['DOWN'],
  jump: ['ALT', 'SPACE'],
  attack: ['CTRL'],
  skill1: ['SHIFT'],
  skill2: ['A'],
  skill3: ['F'],
  loot: ['Z'],
};

const DEFAULT: UserSettings = {
  shakeMul: 1,
  damageScale: 1,
  binds: { ...DEFAULT_BINDS, right: [...DEFAULT_BINDS.right] },
};

function cloneDefault(): UserSettings {
  const binds = {} as Record<BindAction, string[]>;
  for (const k of BIND_ORDER) binds[k] = [...DEFAULT_BINDS[k]];
  return { shakeMul: 1, damageScale: 1, binds };
}

let capturing = false;
let current: UserSettings = cloneDefault();

export function isSettingsCapturing(): boolean {
  return capturing;
}

export function setSettingsCapturing(on: boolean): void {
  capturing = on;
}

function parse(raw: unknown): UserSettings {
  const next = cloneDefault();
  if (!raw || typeof raw !== 'object') return next;
  const rec = raw as Record<string, unknown>;
  if (typeof rec.shakeMul === 'number') next.shakeMul = Math.min(1, Math.max(0, rec.shakeMul));
  if (typeof rec.damageScale === 'number') next.damageScale = Math.min(1.8, Math.max(0.6, rec.damageScale));
  if (rec.binds && typeof rec.binds === 'object') {
    const b = rec.binds as Record<string, unknown>;
    for (const k of BIND_ORDER) {
      if (Array.isArray(b[k]) && typeof b[k][0] === 'string') {
        next.binds[k] = (b[k] as unknown[]).filter((x) => typeof x === 'string') as string[];
      }
    }
  }
  return next;
}

export function settings(): UserSettings {
  return current;
}

export function loadSettings(): UserSettings {
  try {
    const text = localStorage.getItem(KEY);
    if (text) current = parse(JSON.parse(text) as unknown);
  } catch {
    current = cloneDefault();
  }
  return current;
}

export function saveSettings(patch: Partial<UserSettings>): UserSettings {
  current = {
    shakeMul: patch.shakeMul ?? current.shakeMul,
    damageScale: patch.damageScale ?? current.damageScale,
    binds: patch.binds ?? current.binds,
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* 프라이빗 모드 */
  }
  return current;
}

export function resetSettings(): UserSettings {
  current = cloneDefault();
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    /* */
  }
  return current;
}

export function eventToBindName(ev: KeyboardEvent): string | null {
  if (ev.repeat) return null;
  const k = ev.key;
  if (k === ' ') return 'SPACE';
  if (k === 'ArrowLeft') return 'LEFT';
  if (k === 'ArrowRight') return 'RIGHT';
  if (k === 'ArrowUp') return 'UP';
  if (k === 'ArrowDown') return 'DOWN';
  if (k === 'Control') return 'CTRL';
  if (k === 'Alt') return 'ALT';
  if (k === 'Shift') return 'SHIFT';
  if (k.length === 1 && /[a-zA-Z0-9]/.test(k)) return k.toUpperCase();
  return null;
}

void DEFAULT;
